// ScrollWindowList — a lightweight fixed-row-height virtualised list. The
// ranked ALL view has ~1000 instruments, each with an SVG bias bar; rendering
// them all mounts ~1000 theme-subscribed SVGs. This caps the container height
// and renders only the visible slice (+ overscan), with spacer rows preserving
// the scrollbar, so the DOM stays small regardless of row count.

import { ReactNode, useState } from 'react';
import { Box } from '@mui/material';

import { computeWindow } from '../../../../providers/options-flow/options-flow.helpers';
import { themedScrollbarSx } from '../table-chrome';

interface ScrollWindowListProps<T> {
  items: T[];
  rowHeight: number;
  height: number;
  renderRow: (item: T, index: number) => ReactNode;
  keyFor: (item: T, index: number) => string;
  overscan?: number;
  testId?: string;
}

export function ScrollWindowList<T>({
  items,
  rowHeight,
  height,
  renderRow,
  keyFor,
  overscan = 6,
  testId,
}: ScrollWindowListProps<T>) {
  const [scrollTop, setScrollTop] = useState(0);
  const { start, end, padTop, padBottom } = computeWindow(
    scrollTop,
    height,
    rowHeight,
    items.length,
    overscan,
  );
  const visible = items.slice(start, end);

  return (
    <Box
      data-testid={testId}
      sx={{ height, overflowY: 'auto', overflowX: 'hidden', ...themedScrollbarSx }}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
    >
      {padTop > 0 && <Box style={{ height: padTop }} />}
      {visible.map((item, i) => (
        <Box key={keyFor(item, start + i)} style={{ height: rowHeight }}>
          {renderRow(item, start + i)}
        </Box>
      ))}
      {padBottom > 0 && <Box style={{ height: padBottom }} />}
    </Box>
  );
}
